'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/services/api';
import { type MetricId } from '../lib/metric-definitions';
import { buildBuckets, pickGranularity, aggregateByBucket } from '../lib/time-bucketing';
import { isoDate, startOfDay, addDays } from '../lib/format';

export interface MetricSeriesPoint {
  t: Date;
  v: number;
  label: string;
}

export interface MetricData {
  id: MetricId;
  current: number;
  previous: number;
  deltaPct: number;
  series: MetricSeriesPoint[];
  prevSeries: MetricSeriesPoint[];
  isSnapshot: boolean;
}

export interface DateRange {
  from: Date;
  to: Date;
}

interface UseMetricsDataArgs {
  activeMetrics: MetricId[];
  primary: DateRange;
  compare: DateRange | null;
}

export function useMetricsData({ activeMetrics, primary, compare }: UseMetricsDataArgs) {
  const pFrom = startOfDay(primary.from);
  const pTo = startOfDay(primary.to);
  const primaryDays = Math.max(1, Math.round((pTo.getTime() - pFrom.getTime()) / 86400000) + 1);

  const cFrom = compare ? startOfDay(compare.from) : null;
  const cTo = compare ? startOfDay(compare.to) : null;

  const granularity = pickGranularity(primaryDays);
  const buckets = buildBuckets(pFrom, pTo, granularity);
  const prevBuckets = cFrom && cTo ? buildBuckets(cFrom, cTo, granularity) : [];

  // Fetch range covers both primary + compare
  const fetchFrom = cFrom && cFrom < pFrom ? cFrom : pFrom;
  const fetchTo = cTo && cTo > pTo ? cTo : pTo;

  const invoicesQ = useQuery({
    queryKey: ['monitor', 'invoices', 'outgoing', isoDate(fetchFrom), isoDate(fetchTo)],
    queryFn: () =>
      api.get<{ data: any[] }>(
        '/invoices?' +
          new URLSearchParams({
            limit: '2000',
            direction: 'outgoing',
            dateFrom: isoDate(fetchFrom),
            dateTo: isoDate(fetchTo),
          }).toString(),
      ),
    staleTime: 5 * 60 * 1000,
  });

  const expensesQ = useQuery({
    queryKey: ['monitor', 'invoices', 'incoming', isoDate(fetchFrom), isoDate(fetchTo)],
    queryFn: () =>
      api.get<{ data: any[] }>(
        '/invoices?' +
          new URLSearchParams({
            limit: '2000',
            direction: 'incoming',
            dateFrom: isoDate(fetchFrom),
            dateTo: isoDate(fetchTo),
          }).toString(),
      ),
    staleTime: 5 * 60 * 1000,
    enabled: activeMetrics.includes('expenses'),
  });

  const paymentsQ = useQuery({
    queryKey: ['monitor', 'payments', isoDate(fetchFrom), isoDate(fetchTo)],
    queryFn: () =>
      api.get<any>(
        '/payments?' +
          new URLSearchParams({
            limit: '2000',
            dateFrom: isoDate(fetchFrom),
            dateTo: isoDate(fetchTo),
          }).toString(),
      ).catch(() => ({ data: [] })),
    staleTime: 5 * 60 * 1000,
    enabled: activeMetrics.includes('payments'),
  });

  const invoiceStatsQ = useQuery({
    queryKey: ['invoices', 'stats'],
    queryFn: () => api.get<any>('/invoices/stats').catch(() => ({ unpaidTotal: 0 })),
    staleTime: 2 * 60 * 1000,
    enabled: activeMetrics.includes('debitors'),
  });

  const suppliersQ = useQuery({
    queryKey: ['payments', 'suppliers-balance'],
    queryFn: () =>
      api.get<any[]>('/payments/suppliers/balance').catch(() => [] as any[]),
    staleTime: 2 * 60 * 1000,
    enabled: activeMetrics.includes('suppliers'),
  });

  const stockQ = useQuery({
    queryKey: ['stock', 'stats'],
    queryFn: () =>
      api.get<any>('/stock/stats').catch(() => ({ stockValue: { _sum: { totalValue: 0 } } })),
    staleTime: 10 * 60 * 1000,
    enabled: activeMetrics.includes('stock'),
  });

  const clientsQ = useQuery({
    queryKey: ['monitor', 'clients'],
    queryFn: () =>
      api.get<any>('/clients?limit=500&sort=createdAt&order=desc').catch(() => ({ data: [] })),
    staleTime: 10 * 60 * 1000,
    enabled: activeMetrics.includes('newClients'),
  });

  const metrics = useMemo<Record<string, MetricData>>(() => {
    const result: Record<string, MetricData> = {};
    const invArr = (invoicesQ.data?.data || []) as any[];
    const expArr = (expensesQ.data?.data || []) as any[];
    const payRaw = paymentsQ.data;
    const payArr = (Array.isArray(payRaw) ? payRaw : (payRaw?.data || [])) as any[];

    const computeMetric = (
      id: MetricId,
      items: any[],
      getDate: (i: any) => Date | null,
      getValue: (i: any) => number,
      filterFn?: (i: any) => boolean,
    ): MetricData => {
      const filtered = filterFn ? items.filter(filterFn) : items;

      const pBuckets = aggregateByBucket(filtered, getDate, getValue, buckets);
      const current = pBuckets.reduce((s, v) => s + v, 0);
      const series = buckets.map((b, i) => ({ t: b.start, v: pBuckets[i] || 0, label: b.label }));

      let prevSum = 0;
      let prevSeries: MetricSeriesPoint[] = [];
      if (prevBuckets.length > 0) {
        const pvBuckets = aggregateByBucket(filtered, getDate, getValue, prevBuckets);
        prevSum = pvBuckets.reduce((s, v) => s + v, 0);
        // Align prev series to primary buckets indices (one-to-one mapping)
        prevSeries = buckets.map((b, i) => ({
          t: b.start,
          v: pvBuckets[i] !== undefined ? pvBuckets[i]! : 0,
          label: prevBuckets[i]?.label || b.label,
        }));
      }

      const deltaPct = prevSum > 0 ? ((current - prevSum) / prevSum) * 100 : 0;
      return { id, current, previous: prevSum, deltaPct, series, prevSeries, isSnapshot: false };
    };

    if (activeMetrics.includes('sales')) {
      result.sales = computeMetric(
        'sales',
        invArr,
        (i) => (i.issueDate ? new Date(i.issueDate) : null),
        (i) => Number(i.total || 0),
        (i) => i.type === 'factura' && i.status !== 'cancelled' && i.status !== 'draft',
      );
    }
    if (activeMetrics.includes('proforma')) {
      result.proforma = computeMetric(
        'proforma',
        invArr,
        (i) => (i.issueDate ? new Date(i.issueDate) : null),
        (i) => Number(i.total || 0),
        (i) => i.type === 'proforma' && i.status !== 'cancelled' && i.status !== 'draft',
      );
    }
    if (activeMetrics.includes('aviz')) {
      result.aviz = computeMetric(
        'aviz',
        invArr,
        (i) => (i.issueDate ? new Date(i.issueDate) : null),
        (i) => Number(i.total || 0),
        (i) => i.type === 'aviz' && i.status !== 'cancelled' && i.status !== 'draft',
      );
    }
    if (activeMetrics.includes('expenses')) {
      result.expenses = computeMetric(
        'expenses',
        expArr,
        (i) => (i.issueDate ? new Date(i.issueDate) : null),
        (i) => Number(i.total || 0),
        (i) => i.status !== 'cancelled' && i.status !== 'draft',
      );
    }
    if (activeMetrics.includes('payments')) {
      result.payments = computeMetric(
        'payments',
        payArr,
        (i) => {
          const raw = i.date || i.paymentDate || i.createdAt;
          return raw ? new Date(raw) : null;
        },
        (i) => Number(i.amount || 0),
      );
    }
    if (activeMetrics.includes('invoiceCount')) {
      result.invoiceCount = computeMetric(
        'invoiceCount',
        invArr,
        (i) => (i.issueDate ? new Date(i.issueDate) : null),
        () => 1,
        (i) => i.type === 'factura' && i.status !== 'cancelled' && i.status !== 'draft',
      );
    }
    if (activeMetrics.includes('newClients')) {
      const clientsArr = (clientsQ.data?.data || clientsQ.data || []) as any[];
      result.newClients = computeMetric(
        'newClients',
        clientsArr,
        (c) => (c.createdAt ? new Date(c.createdAt) : null),
        () => 1,
      );
    }

    // Snapshot metrics
    const makeSnapshot = (id: MetricId, value: number): MetricData => {
      const flatSeries = buckets.map((b) => ({ t: b.start, v: value, label: b.label }));
      const flatPrev = buckets.map((b) => ({ t: b.start, v: value, label: b.label }));
      return { id, current: value, previous: value, deltaPct: 0, series: flatSeries, prevSeries: flatPrev, isSnapshot: true };
    };
    if (activeMetrics.includes('debitors')) {
      const unpaid = Number(invoiceStatsQ.data?.unpaidTotal || 0);
      result.debitors = makeSnapshot('debitors', unpaid);
    }
    if (activeMetrics.includes('suppliers')) {
      const suppArr = (suppliersQ.data || []) as any[];
      const total = suppArr.reduce((s, sup) => s + Number(sup.totalDebt || 0), 0);
      result.suppliers = makeSnapshot('suppliers', Math.max(0, total));
    }
    if (activeMetrics.includes('stock')) {
      const stockVal = Number(
        stockQ.data?.totalValue || 0,
      );
      result.stock = makeSnapshot('stock', stockVal);
    }

    return result;
  }, [
    activeMetrics,
    invoicesQ.data,
    expensesQ.data,
    paymentsQ.data,
    invoiceStatsQ.data,
    suppliersQ.data,
    stockQ.data,
    clientsQ.data,
    buckets,
    prevBuckets,
  ]);

  return {
    metrics,
    isLoading: invoicesQ.isLoading || expensesQ.isLoading || paymentsQ.isLoading,
    from: pFrom,
    to: pTo,
    buckets,
    granularity,
  };
}
